import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "bar", "fieldSelect", "valuePanel", "valueInput", "status", "rowCheckbox", "selectAll", "selectionSummary",
    "applyButton"
  ]
  static values = { rowSelector: String }

  connect() {
    this.fieldChanged()
    this.selectionChanged()
  }

  rowCheckboxTargetConnected() {
    this.selectionChanged()
  }

  fieldChanged() {
    if (!this.hasFieldSelectTarget) return

    const field = this.fieldSelectTarget.value
    this.valuePanelTargets.forEach((panel) => {
      panel.classList.toggle("hidden", panel.dataset.bulkField !== field)
    })
    this.updateStatus("")
    this.updateApplyButton()
  }

  valueChanged() {
    window.requestAnimationFrame(() => {
      if (this.currentValueInput()?.value) this.updateStatus("")
      this.updateApplyButton()
    })
  }

  apply(event) {
    event.preventDefault()

    const field = this.fieldSelectTarget.value
    const input = this.currentValueInput()
    if (!input || !input.value) {
      this.updateStatus("Pasirinkite reikšmę")
      this.updateApplyButton()
      return
    }

    const displayText = this.displayText(input, field)
    const changes = this.selectedRows().map((row) => {
      return this.applyFieldToRow(row, field, input.value, displayText)
    }).filter(Boolean)
    const changedCount = changes.length

    this.updateStatus(changedCount > 0 ? "" : "Nėra pažymėtų eilučių")
    if (changedCount > 0) {
      this.flashChangedCells(changes)
    }
  }

  toggleAll(event) {
    this.visibleRowCheckboxes().forEach((checkbox) => {
      checkbox.checked = event.currentTarget.checked
      this.updateRowState(checkbox)
    })
    this.selectionChanged()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  selectionChanged(event) {
    if (event?.currentTarget) this.updateRowState(event.currentTarget)

    const selectedCount = this.selectedRows().length
    if (this.hasSelectionSummaryTarget) this.selectionSummaryTarget.textContent = this.selectionSummary(selectedCount)

    this.toggleBar(selectedCount > 0)
    this.updateSelectAllState()
    this.updateApplyButton()
    if (selectedCount === 0) this.updateStatus("")
  }

  visibleRows() {
    return Array.from(this.element.querySelectorAll(this.rowSelectorValue)).filter((row) => {
      const destroyInput = row.querySelector('input[name*="[_destroy]"]')
      return row.style.display !== "none" && destroyInput?.value !== "1"
    })
  }

  visibleRowCheckboxes() {
    return this.visibleRows()
      .map((row) => row.querySelector('[data-bulk-line-update-target="rowCheckbox"]'))
      .filter(Boolean)
  }

  selectedRows() {
    return this.visibleRows().filter((row) => {
      const checkbox = row.querySelector('[data-bulk-line-update-target="rowCheckbox"]')
      return checkbox?.checked
    })
  }

  currentValueInput() {
    if (!this.hasFieldSelectTarget) return null

    const field = this.fieldSelectTarget.value
    return this.valueInputTargets.find((valueInput) => valueInput.dataset.bulkField === field)
  }

  canApply() {
    return this.selectedRows().length > 0 && !!this.currentValueInput()?.value
  }

  applyFieldToRow(row, field, value, displayText) {
    if (field === "vat_classification_id") {
      const input = row.querySelector('input[name*="[vat_classification_id]"]')
      if (!input) return false
      const before = this.captureFieldState(input, "form-vat-selector")

      input.value = value
      this.updateSelectorDisplay(input, "form-vat-selector", displayText)
      input.dispatchEvent(new Event("change", { bubbles: true }))
      return { ...before, input, controllerName: "form-vat-selector" }
    }

    if (field.startsWith("lookup:")) {
      const [, perspective, lookupType] = field.split(":")
      const input = row.querySelector(`input[name*="[custom_fields][${perspective}_${lookupType}_id]"]`)
      if (!input) return false
      const before = this.captureFieldState(input, "lookup-selector")

      input.value = value
      this.updateSelectorDisplay(input, "lookup-selector", displayText)
      input.dispatchEvent(new Event("change", { bubbles: true }))
      this.dispatchLookupMemoryChange(input, value, displayText, perspective, lookupType)
      return { ...before, input, controllerName: "lookup-selector" }
    }

    return false
  }

  captureFieldState(input, controllerName) {
    const selector = input.closest(`[data-controller~="${controllerName}"]`)
    const targetName = controllerName === "form-vat-selector" ? "form-vat-selector" : "lookup-selector"
    const placeholder = selector?.querySelector(`[data-${targetName}-target="placeholder"]`)

    return {
      oldValue: input.value,
      oldDisplayText: placeholder?.textContent?.trim() || "—"
    }
  }

  displayText(input, field) {
    const selector = input.closest('[data-controller~="form-vat-selector"], [data-controller~="lookup-selector"]')
    const placeholder = selector?.querySelector(
      '[data-form-vat-selector-target="placeholder"], [data-lookup-selector-target="placeholder"]'
    )
    const text = placeholder?.textContent?.trim() || ""

    if (field === "vat_classification_id") return text.split(" (")[0]

    return text
  }

  updateSelectorDisplay(input, controllerName, displayText) {
    const selector = input.closest(`[data-controller~="${controllerName}"]`)
    const targetName = controllerName === "form-vat-selector" ? "form-vat-selector" : "lookup-selector"
    const placeholder = selector?.querySelector(`[data-${targetName}-target="placeholder"]`)
    if (!placeholder) return

    placeholder.textContent = displayText || "—"
    placeholder.classList.remove("text-gray-500")
    placeholder.classList.add("text-black", "font-medium")
  }

  dispatchLookupMemoryChange(input, lookupId, displayText, perspective, lookupType) {
    const selector = input.closest('[data-controller~="lookup-selector"]')
    const memoryKey = selector?.dataset.lookupSelectorMemoryKeyValue
    if (!memoryKey) return

    const normalizedLookupId = lookupId ? String(lookupId) : ""
    const initialLookupId = selector?.dataset.lookupSelectorInitialLookupId || ""

    document.dispatchEvent(new CustomEvent("lookup-selector:change", {
      detail: {
        memoryKey,
        lookupId: normalizedLookupId,
        displayText: displayText || "",
        tipas: "",
        lookupType,
        perspective,
        touched: normalizedLookupId !== initialLookupId
      }
    }))
  }

  flashChangedCells(changes) {
    changes.forEach((change) => {
      const row = change.input.closest(this.rowSelectorValue)
      const selector = change.input.closest(`[data-controller~="${change.controllerName}"]`)
      const button = selector?.querySelector("[data-form-vat-selector-target='selectButton'], [data-lookup-selector-target='selectButton']")

      this.restartAnimation(row, "bulk-line-updated-row")
      this.restartAnimation(button, "bulk-line-updated-cell")
    })
  }

  restartAnimation(element, className) {
    if (!element) return

    element.classList.remove(className)
    void element.offsetWidth
    element.classList.add(className)
    window.setTimeout(() => {
      element.classList.remove(className)
    }, 1400)
  }

  updateStatus(message) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = message
  }

  toggleBar(visible) {
    if (!this.hasBarTarget) return

    this.barTarget.classList.toggle("hidden", !visible)
    this.barTarget.classList.toggle("flex", visible)
  }

  updateApplyButton() {
    if (!this.hasApplyButtonTarget) return

    const count = this.selectedRows().length
    this.applyButtonTarget.textContent = count > 0 ? this.applyLabel(count) : "Taikyti"
    this.applyButtonTarget.disabled = !this.canApply()
  }

  updateSelectAllState() {
    if (!this.hasSelectAllTarget) return

    const checkboxes = this.visibleRowCheckboxes()
    const checkedCount = checkboxes.filter((checkbox) => checkbox.checked).length
    this.selectAllTarget.checked = checkboxes.length > 0 && checkedCount === checkboxes.length
    this.selectAllTarget.indeterminate = checkedCount > 0 && checkedCount < checkboxes.length
  }

  updateRowState(checkbox) {
    const row = checkbox.closest(this.rowSelectorValue)
    row?.classList.remove("bg-sky-100/70")
    row?.classList.toggle("bg-gray-50", checkbox.checked)
  }

  selectionSummary(count) {
    if (count === 1) return "1 eilutė pažymėta"

    return `${count} eilutės pažymėtos`
  }

  applyLabel(count) {
    if (count === 1) return "Taikyti 1 eilutei"

    return `Taikyti ${count} eilutėms`
  }

  fieldLabel(field) {
    if (field === "vat_classification_id") return "PVM klasė"

    const option = Array.from(this.fieldSelectTarget.options).find((fieldOption) => fieldOption.value === field)
    return option?.textContent?.trim() || "Laukas"
  }
}
